/**
 * References Store
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { api } from '@/lib/axios';
import { REFERENCES } from '@/api/endpoints';
import type { Reference, LocationReference, EnrichmentTypeReference } from '@/types';

// Transform API reference item to frontend Reference type
interface ApiReferenceItem {
  codigo: string;
  descripcion: string;
  extra?: unknown;
}

function transformReference(item: ApiReferenceItem): Reference {
  return {
    id: item.codigo,
    code: item.codigo,
    name: item.descripcion,
    description: item.descripcion,
  };
}

function extractItems(response: { data: { data?: { items?: ApiReferenceItem[] } } }): Reference[] {
  const items = response.data?.data?.items;
  if (Array.isArray(items)) {
    return items.map(transformReference);
  }
  return [];
}

interface ReferencesState {
  // Data
  departamentos: Reference[];
  provincias: Record<string, LocationReference[]>;
  distritos: Record<string, LocationReference[]>;
  estadosReinfo: Reference[];
  estadosCatastro: Reference[];
  leyendas: Reference[];
  tiposActividad: Reference[];
  sustancias: Reference[];
  enrichmentTypes: EnrichmentTypeReference[];
  satelliteSources: Reference[];
  
  // State
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  loadReferences: () => Promise<void>;
  loadProvincias: (departamento: string) => Promise<void>;
  loadDistritos: (provincia: string) => Promise<void>;
  
  // Getters
  getDepartamentoName: (code: string) => string;
  getProvinciaName: (code: string) => string;
  getDistritoName: (code: string) => string;
}

export const useReferencesStore = create<ReferencesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      departamentos: [],
      provincias: {},
      distritos: {},
      estadosReinfo: [],
      estadosCatastro: [],
      leyendas: [],
      tiposActividad: [],
      sustancias: [],
      enrichmentTypes: [],
      satelliteSources: [],
      
      isLoaded: false,
      isLoading: false,
      error: null,
      
      // Load all base references
      loadReferences: async () => {
        if (get().isLoaded || get().isLoading) return;
        
        set({ isLoading: true, error: null });
        
        try {
          const [
            departamentosRes,
            estadosReinfoRes,
            estadosCatastroRes,
            leyendasRes,
            tiposActividadRes,
          ] = await Promise.all([
            api.get(REFERENCES.TYPE('departamentos')),
            api.get(REFERENCES.TYPE('estados-reinfo')),
            api.get(REFERENCES.TYPE('estados-catastro')),
            api.get(REFERENCES.TYPE('leyendas')),
            api.get(REFERENCES.TYPE('tipos-actividad')),
          ]);
          
          set({
            departamentos: extractItems(departamentosRes),
            estadosReinfo: extractItems(estadosReinfoRes),
            estadosCatastro: extractItems(estadosCatastroRes),
            leyendas: extractItems(leyendasRes),
            tiposActividad: extractItems(tiposActividadRes),
            isLoaded: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: 'Failed to load references',
            isLoading: false,
          });
          console.error('Failed to load references:', error);
        }
      },
      
      // Load provincias for a departamento
      loadProvincias: async (departamento: string) => {
        if (get().provincias[departamento]) return;
        
        try {
          const res = await api.get(REFERENCES.TYPE('provincias'), {
            params: { departamento },
          });
          
          set((state) => ({
            provincias: {
              ...state.provincias,
              [departamento]: extractItems(res) as LocationReference[],
            },
          }));
        } catch (error) {
          console.error(`Failed to load provincias for ${departamento}:`, error);
        }
      },
      
      // Load distritos for a provincia
      loadDistritos: async (provincia: string) => {
        if (get().distritos[provincia]) return;
        
        try {
          const res = await api.get(REFERENCES.TYPE('distritos'), {
            params: { provincia },
          });
          
          set((state) => ({
            distritos: {
              ...state.distritos,
              [provincia]: extractItems(res) as LocationReference[],
            },
          }));
        } catch (error) {
          console.error(`Failed to load distritos for ${provincia}:`, error);
        }
      },
      
      // Get departamento name by code
      getDepartamentoName: (code: string) => {
        const dep = get().departamentos.find((d) => d.code === code);
        return dep?.name ?? code;
      },
      
      // Get provincia name by code
      getProvinciaName: (code: string) => {
        for (const provincias of Object.values(get().provincias)) {
          const prov = provincias.find((p) => p.code === code);
          if (prov) return prov.name;
        }
        return code;
      },
      
      // Get distrito name by code
      getDistritoName: (code: string) => {
        for (const distritos of Object.values(get().distritos)) {
          const dist = distritos.find((d) => d.code === code);
          if (dist) return dist.name;
        }
        return code;
      },
    }),
    { name: 'ReferencesStore' }
  )
);

// Selectors
export const selectDepartamentos = (state: ReferencesState) => state.departamentos;
export const selectEstadosReinfo = (state: ReferencesState) => state.estadosReinfo;
export const selectLeyendas = (state: ReferencesState) => state.leyendas;
export const selectIsLoaded = (state: ReferencesState) => state.isLoaded;
